import {
  EditOutlined,
  FilePdfOutlined,
  FilterOutlined,
  MailOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Alert, Button, Checkbox, Flex, Input, Select, Space, Spin, Table, Tag, Tooltip } from 'antd';
import React, { useMemo } from 'react';
import type { ClientInvoiceSummary, SagaCompany } from '../../DTOS/FacturiScadente';

const validRegistrationNumberRegex =
  /^(?:[JFC]\s*(?:0[1-9]|[1-3]\d|40|51|52)\s*\/\s*\d{1,6}\s*\/\s*(?:19|20)\d{2}|[JFC](?:19|20)\d{2}\d{6}(?:00|0[1-9]|[1-3]\d|40|51|52)\d)$/i;
const normalizeCuiKey = (value?: string): string => (value ? value.replace(/\D/g, '') : '');

interface ClientiTabProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  categoryFilter: string;
  onCategoryFilterChange: (value: string) => void;
  emailFilterMode: '' | 'with' | 'without';
  onEmailFilterModeChange: (value: '' | 'with' | 'without') => void;
  hasOutstandingOnly: boolean;
  onHasOutstandingChange: (value: boolean) => void;
  notificationsEnabledOnly: boolean;
  onNotificationsEnabledChange: (value: boolean) => void;
  onRefresh: () => void;
  onOpenBulkEdit: () => void;
  onRunBulkNotification: () => void;
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
  clients: SagaCompany[];
  totalItems: number;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  onEditClient: (client: SagaCompany) => void;
  onPreviewClientNotification: (client: SagaCompany) => void;
  onPreviewClientNotificationPdf: (client: SagaCompany) => void;
  isPreviewingClientNotificationId?: string | null;
  isPreviewingClientNotificationPdfId?: string | null;
  isRunningClientNotificationId?: string | null;
  isRunningBulkNotification?: boolean;
  summaryByCui: Map<string, ClientInvoiceSummary>;
  isClientSummaryLoading: boolean;
  toNumber: (value: unknown) => number;
}

const ClientiTab: React.FC<ClientiTabProps> = ({
  searchTerm,
  onSearchChange,
  categoryFilter,
  onCategoryFilterChange,
  emailFilterMode,
  onEmailFilterModeChange,
  hasOutstandingOnly,
  onHasOutstandingChange,
  notificationsEnabledOnly,
  onNotificationsEnabledChange,
  onRefresh,
  onOpenBulkEdit,
  onRunBulkNotification,
  selectedRowKeys,
  setSelectedRowKeys,
  clients,
  totalItems,
  isLoading,
  isError,
  errorMessage,
  page,
  pageSize,
  onPageChange,
  onEditClient,
  onPreviewClientNotification,
  onPreviewClientNotificationPdf,
  isPreviewingClientNotificationId,
  isPreviewingClientNotificationPdfId,
  isRunningClientNotificationId,
  isRunningBulkNotification,
  summaryByCui,
  isClientSummaryLoading,
  toNumber,
}) => {
  const toggleRowSelection = (key: React.Key, selected: boolean) => {
    setSelectedRowKeys((prev) => {
      if (selected) {
        return prev.includes(key) ? prev : [...prev, key];
      }
      return prev.filter((k) => k !== key);
    });
  };

  const clientColumns = useMemo(
    () => [
      {
        title: 'Client / CUI',
        dataIndex: 'name',
        key: 'name',
        render: (_: string, record: SagaCompany) => (
          <div>
            <div>{record.name || '-'}</div>
            <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>{record.cui}</div>
          </div>
        ),
      },
      {
        title: 'Nr. Reg. Com.',
        dataIndex: 'registrationNumber',
        key: 'registrationNumber',
        render: (value: string) => {
          const normalizedValue = value?.trim();
          return normalizedValue && validRegistrationNumberRegex.test(normalizedValue) ? normalizedValue : '-';
        },
      },
      {
        title: 'Categorie',
        dataIndex: 'category',
        key: 'category',
        render: (value: string) => value || 'fara categorie',
      },
      {
        title: 'Email',
        key: 'emailCombined',
        render: (_: unknown, record: SagaCompany) => (
          <div>
            <div>
              <span style={{ fontWeight: 500 }}>To:</span>{' '}
              {record.email ? record.email : <Tag color='red'>Lipseste</Tag>}
            </div>
            <div>
              <span style={{ fontWeight: 500 }}>Bcc:</span> {record.billingEmail || '-'}
            </div>
          </div>
        ),
      },
      {
        title: 'Telefon',
        dataIndex: 'phone',
        key: 'phone',
        render: (value: string) => value || '-',
      },
      {
        title: 'Total restant',
        key: 'totalOutstanding',
        render: (_: unknown, record: SagaCompany) => {
          if (isClientSummaryLoading) {
            return <Tag>...</Tag>;
          }
          const key = normalizeCuiKey(record.cui);
          const summary = (key ? summaryByCui.get(key) : undefined) ?? undefined;
          if (!summary) {
            return '-';
          }
          const total = toNumber(summary.totalOutstanding);
          if (total <= 0) {
            return '-';
          }
          return (
            <div>
              <div>{total.toLocaleString('ro-RO')}</div>
              {summary.totals ? <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>{summary.totals}</div> : null}
            </div>
          );
        },
      },
      {
        title: 'Urmeaza la scadenta',
        key: 'upcomingOutstanding',
        render: (_: unknown, record: SagaCompany) => {
          if (isClientSummaryLoading) {
            return <Tag>...</Tag>;
          }
          const key = normalizeCuiKey(record.cui);
          const summary = (key ? summaryByCui.get(key) : undefined) ?? undefined;
          if (!summary) {
            return '-';
          }

          const upcoming = toNumber(summary.upcomingOutstanding);
          if (upcoming <= 0) {
            return '-';
          }

          return (
            <div>
              <div>{upcoming.toLocaleString('ro-RO')}</div>
              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>{summary.upcomingInvoiceCount ?? 0} facturi</div>
              {summary.upcomingTotals ? (
                <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>{summary.upcomingTotals}</div>
              ) : null}
            </div>
          );
        },
      },
      {
        title: 'Notificari',
        dataIndex: 'isEmailNotificationsEnabled',
        key: 'isEmailNotificationsEnabled',
        render: (value: boolean) => (value ? <Tag color='green'>Active</Tag> : <Tag color='red'>Oprite</Tag>),
      },
      {
        title: 'PDF notificare',
        dataIndex: 'attachPaymentNotificationPdf',
        key: 'attachPaymentNotificationPdf',
        render: (value: boolean, record: SagaCompany) =>
          value ? (
            <Tag color='blue'>
              {record.attachPaymentNotificationPdfMode === 'once' ? 'O singura data' : 'La fiecare'}
            </Tag>
          ) : (
            <Tag>-</Tag>
          ),
      },
      {
        title: 'Actiuni',
        key: 'actions',
        render: (_: unknown, record: SagaCompany) => {
          const key = normalizeCuiKey(record.cui);
          const summary = (key ? summaryByCui.get(key) : undefined) ?? undefined;
          const hasOutstanding =
            !!summary && toNumber(summary.totalOutstanding) + toNumber(summary.upcomingOutstanding) > 0;
          const canRun = !!record.isEmailNotificationsEnabled;

          return (
            <Space>
              <Tooltip title='Editeaza'>
                <Button size='small' icon={<EditOutlined />} onClick={() => onEditClient(record)}></Button>
              </Tooltip>
              <Tooltip title='Trimite'>
                <Button
                  size='small'
                  icon={<MailOutlined />}
                  disabled={!canRun}
                  loading={
                    isPreviewingClientNotificationId === record.id || isRunningClientNotificationId === record.id
                  }
                  onClick={() => onPreviewClientNotification(record)}
                ></Button>
              </Tooltip>

              {hasOutstanding && (
                <Tooltip title='Vizualizeaza PDF Notificare de Plata'>
                  <Button
                    size='small'
                    icon={<FilePdfOutlined />}
                    loading={isPreviewingClientNotificationPdfId === record.id}
                    onClick={() => onPreviewClientNotificationPdf(record)}
                  ></Button>
                </Tooltip>
              )}
            </Space>
          );
        },
      },
    ],
    [
      onEditClient,
      onPreviewClientNotification,
      onPreviewClientNotificationPdf,
      isPreviewingClientNotificationId,
      isPreviewingClientNotificationPdfId,
      isRunningClientNotificationId,
      isClientSummaryLoading,
      summaryByCui,
      toNumber,
    ],
  );

  return (
    <div className='table-page-no-h-scroll'>
      <Flex vertical gap={12} style={{ marginBottom: 16 }}>
        <Flex wrap='wrap' justify='space-between' align='center' gap={12}>
          <Input.Search
            placeholder='Cauta dupa nume sau CUI'
            allowClear
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ width: 480, maxWidth: '100%' }}
            prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
          />
          <Space wrap size='middle'>
            <Button icon={<ReloadOutlined />} onClick={onRefresh}>
              Reincarca
            </Button>
            <Button icon={<EditOutlined />} disabled={selectedRowKeys.length === 0} onClick={onOpenBulkEdit}>
              Editare multipla ({selectedRowKeys.length})
            </Button>
            <Button disabled={selectedRowKeys.length === 0} onClick={() => setSelectedRowKeys([])}>
              Deselecteaza tot
            </Button>
            <Button
              icon={<MailOutlined />}
              disabled={selectedRowKeys.length === 0}
              loading={isRunningBulkNotification}
              onClick={onRunBulkNotification}
            >
              Trimite notificari ({selectedRowKeys.length})
            </Button>
          </Space>
        </Flex>

        <Flex
          wrap='wrap'
          align='center'
          gap={16}
          style={{
            padding: '10px 14px',
            background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
            borderRadius: 6,
          }}
        >
          <span style={{ color: 'var(--ant-color-text-secondary)', fontWeight: 500, marginRight: 4 }}>
            <FilterOutlined style={{ marginRight: 6 }} />
            Filtre:
          </span>
          <Select
            placeholder='Categorie'
            allowClear
            value={categoryFilter || ''}
            onChange={(value) => onCategoryFilterChange((value ?? '') as string)}
            style={{ width: 170 }}
            options={[
              { label: 'Toate categoriile', value: '' },
              { label: 'B2G', value: 'B2G' },
              { label: 'B2B', value: 'B2B' },
              { label: 'B2C', value: 'B2C' },
              { label: 'fara categorie', value: 'fara categorie' },
            ]}
          />
          <Select
            placeholder='Filtru email'
            allowClear
            value={emailFilterMode}
            onChange={(value) => onEmailFilterModeChange((value ?? '') as '' | 'with' | 'without')}
            style={{ width: 170 }}
            options={[
              { label: 'Toate emailurile', value: '' },
              { label: 'Cu email', value: 'with' },
              { label: 'Fara email', value: 'without' },
            ]}
          />
          <Checkbox checked={hasOutstandingOnly} onChange={(e) => onHasOutstandingChange(e.target.checked)}>
            Restanti + urmeaza (7 zile)
          </Checkbox>
          <Checkbox checked={notificationsEnabledOnly} onChange={(e) => onNotificationsEnabledChange(e.target.checked)}>
            Notificari active
          </Checkbox>
        </Flex>
      </Flex>

      {isLoading ? (
        <Spin size='large' className='spinner' />
      ) : isError ? (
        <Alert message={errorMessage || 'Eroare la incarcare.'} type='error' />
      ) : (
        <div className='table-scroll-wrapper'>
          <Table
            rowKey='id'
            columns={clientColumns}
            dataSource={clients}
            rowSelection={{
              selectedRowKeys,
              onSelect: (record, selected) => toggleRowSelection(record.id, selected),
              onSelectAll: (selected, _selectedRows, changeRows) => {
                setSelectedRowKeys((prev) => {
                  const changeIds = changeRows.map((row) => row.id);
                  if (selected) {
                    const merged = new Set([...prev, ...changeIds]);
                    return Array.from(merged);
                  }
                  return prev.filter((id) => !changeIds.includes(id as string));
                });
              },
            }}
            scroll={{ x: true }}
            pagination={{
              current: page,
              pageSize,
              total: totalItems,
              onChange: (nextPage, nextPageSize) => onPageChange(nextPage, nextPageSize || pageSize),
              showSizeChanger: true,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ClientiTab;
